import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import moment from "moment";
import { Alert, Empty, Input, message, Radio, Select, Table, Tag } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { ModalItem, ButtonItem, SegmentedItem } from "../../../../Components/Items";
import {
  construirLibroPlantilla,
  descargarLibroPlantilla,
  empleadoAFila,
  hojaInstrucciones,
} from "../ImportarEmpleados/plantillaEmpleados";

/**
 * Exporta empleados con la MISMA plantilla de "Importar Empleados", para darlos de alta en otra
 * empresa subiendo el archivo tal cual (ver plantillaEmpleados.js).
 *
 * La lista sale de `selectTodosEmpleados` con `valor: 3` (todos) y no de los renglones de la
 * pantalla: ahí puede estar puesto el filtro de deshabilitados y aquí siempre se ofrece todo. Los
 * datos completos del Excel se piden hasta el final a `exportarEmpleados`, sólo de los elegidos.
 *
 * Es un `Table` de antd y no `TableItem` porque éste ignora `pagination` y `scroll`.
 *
 * Props: visible, onCancel
 */

const SIN_DEPARTAMENTO = "__sinDepartamento__";

const idDepartamento = (e) => (e.departamentoId?._id ? String(e.departamentoId._id) : SIN_DEPARTAMENTO);

const normalizar = (texto) =>
  String(texto ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Sin código al final; el endpoint los manda por fecha de creación.
const porCodigo = (a, b) => (a.codigo ?? Infinity) - (b.codigo ?? Infinity);

const ESTADO_INICIAL = {
  estatus: 1,           // 1 activos · 2 deshabilitados · 3 todos
  modo: "todos",        // todos · departamento · empleados
  departamentos: [],
  seleccionados: [],
  busqueda: "",
};

class ModalExportarEmpleados extends Component {
  state = {
    cargando: false,
    exportando: false,
    empleados: [],
    ...ESTADO_INICIAL,
  };

  componentDidUpdate(prevProps) {
    // Se consulta en cada apertura: la lista pudo cambiar (altas, bajas) desde la última vez.
    if (this.props.visible && !prevProps.visible) {
      this.setState({ ...ESTADO_INICIAL });
      this.consultarEmpleados();
    }
  }

  consultarEmpleados() {
    this.setState({ cargando: true, empleados: [] });
    axios
      .post(
        "empleados/selectTodosEmpleados",
        { empresaId: this.props.empresaId, valor: 3 },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        // Sin empleados el endpoint responde { status: 404 } en vez de un arreglo.
        const empleados = Array.isArray(res.data) ? [...res.data].sort(porCodigo) : [];
        this.setState({ cargando: false, empleados });
      })
      .catch(() => {
        this.setState({ cargando: false });
        message.error("No se pudieron consultar los empleados");
      });
  }

  empleadosPorEstatus() {
    const { empleados, estatus } = this.state;
    if (estatus === 3) return empleados;
    return empleados.filter((e) => (estatus === 1 ? e.estado === 1 : e.estado !== 1));
  }

  /**
   * En "por empleado" manda la selección explícita, sin importar el filtro de estatus: el filtro
   * sólo acota la tabla, y quitar de la exportación a alguien que el usuario palomeó sería peor.
   */
  empleadosAExportar(porEstatus) {
    const { modo, departamentos, seleccionados, empleados } = this.state;
    if (modo === "departamento") {
      const elegidos = new Set(departamentos);
      return porEstatus.filter((e) => elegidos.has(idDepartamento(e)));
    }
    if (modo === "empleados") {
      const elegidos = new Set(seleccionados);
      return empleados.filter((e) => elegidos.has(String(e._id)));
    }
    return porEstatus;
  }

  opcionesDepartamento(porEstatus) {
    const conteo = new Map();
    for (const e of porEstatus) {
      const id = idDepartamento(e);
      const previo = conteo.get(id) || { nombre: e.departamentoId?.nombre || "Sin departamento", cantidad: 0 };
      previo.cantidad++;
      conteo.set(id, previo);
    }
    return [...conteo.entries()]
      .sort(([idA, a], [idB, b]) => {
        if (idA === SIN_DEPARTAMENTO) return 1;
        if (idB === SIN_DEPARTAMENTO) return -1;
        return a.nombre.localeCompare(b.nombre, "es");
      })
      .map(([id, { nombre, cantidad }]) => ({ value: id, label: `${nombre} (${cantidad})` }));
  }

  filtrarPorBusqueda(filas) {
    const texto = normalizar(this.state.busqueda.trim());
    if (!texto) return filas;
    return filas.filter((e) =>
      [e.codigoTexto, e.nombre, e.rfc, e.departamento, e.puesto].some((campo) => normalizar(campo).includes(texto))
    );
  }

  exportar(lista) {
    if (!lista.length) return;
    this.setState({ exportando: true });
    axios
      .post(
        "empleados/exportarEmpleados",
        { empresaId: this.props.empresaId, empleadosIds: lista.map((e) => e._id) },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ exportando: false });
        if (res.data.success !== 2) {
          message.error(res.data.message || "No se pudieron exportar los empleados");
          return;
        }
        const empleados = res.data.empleados || [];
        const libro = construirLibroPlantilla(empleados.map(empleadoAFila), {
          hojasExtra: [{
            nombre: "Instrucciones",
            hoja: hojaInstrucciones({ totalEmpleados: empleados.length, empresaNombre: this.props.empresaNombre }),
          }],
        });
        descargarLibroPlantilla(libro, `Empleados_${moment().format("YYYY-MM-DD")}.xlsx`);
        message.success(
          empleados.length === 1 ? "Se exportó 1 empleado" : `Se exportaron ${empleados.length} empleados`
        );
        this.props.onCancel();
      })
      .catch(() => {
        this.setState({ exportando: false });
        message.error("Error al exportar los empleados");
      });
  }

  render() {
    const { visible, onCancel } = this.props;
    const { cargando, exportando, estatus, modo, departamentos, seleccionados, busqueda } = this.state;

    const porEstatus = this.empleadosPorEstatus();
    const aExportar = this.empleadosAExportar(porEstatus);
    const deshabilitados = aExportar.filter((e) => e.estado !== 1).length;

    const columnas = [
      { title: "Código", dataIndex: "codigoTexto", key: "codigo", width: 80 },
      { title: "Nombre", dataIndex: "nombre", key: "nombre" },
      { title: "RFC", dataIndex: "rfc", key: "rfc", width: 140 },
      { title: "Departamento", dataIndex: "departamento", key: "departamento", width: 160 },
      {
        title: "Estatus",
        key: "estatus",
        width: 115,
        render: (_, r) => (r.estado === 1 ? <Tag color="green">Activo</Tag> : <Tag>Deshabilitado</Tag>),
      },
    ];

    return (
      <ModalItem
        title="Exportar Empleados"
        visible={visible}
        onCancel={onCancel}
        spinning={cargando || exportando}
      >
        <div style={{ paddingTop: 10 }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="El archivo sale con la plantilla de Importar Empleados: en la otra empresa se sube tal cual desde Empleados → Importar Empleados."
            description="Los catálogos propios de cada empresa (Tipo Periodo, Turno, Tipo Prestación, Banco y Registro Patronal) se buscan por nombre en la empresa destino. La hoja de Instrucciones del archivo explica qué se crea solo y qué no viaja."
          />

          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 12 }}>
            <SegmentedItem
              options={[
                { label: "Activos", value: 1 },
                { label: "Deshabilitados", value: 2 },
                { label: "Todos", value: 3 },
              ]}
              value={estatus}
              onChange={(valor) => this.setState({ estatus: valor })}
            />
            <Radio.Group
              value={modo}
              optionType="button"
              buttonStyle="solid"
              onChange={(e) => this.setState({ modo: e.target.value })}
              options={[
                { label: "Todos", value: "todos" },
                { label: "Por departamento", value: "departamento" },
                { label: "Por empleado", value: "empleados" },
              ]}
            />
          </div>

          {modo === "departamento" && (
            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%", marginBottom: 12 }}
              placeholder="Elige uno o más departamentos"
              value={departamentos}
              onChange={(valores) => this.setState({ departamentos: valores })}
              options={this.opcionesDepartamento(porEstatus)}
              optionFilterProp="label"
            />
          )}

          {modo === "empleados" && (
            <>
              <Input.Search
                allowClear
                style={{ marginBottom: 10 }}
                placeholder="Buscar por código, nombre, RFC, departamento o puesto"
                value={busqueda}
                onChange={(e) => this.setState({ busqueda: e.target.value })}
              />
              <div style={{ overflowX: "auto" }}>
                <Table
                  size="small"
                  rowKey={(r) => String(r._id)}
                  dataSource={this.filtrarPorBusqueda(porEstatus)}
                  columns={columnas}
                  pagination={{ pageSize: 50, showSizeChanger: false }}
                  scroll={{ x: 700, y: 340 }}
                  locale={{ emptyText: <Empty description="No hay empleados con ese filtro" /> }}
                  rowSelection={{
                    selectedRowKeys: seleccionados,
                    preserveSelectedRowKeys: true,
                    selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
                    onChange: (llaves) => this.setState({ seleccionados: llaves }),
                  }}
                />
              </div>
            </>
          )}

          {deshabilitados > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message={
                deshabilitados === 1
                  ? "1 empleado deshabilitado se dará de alta como activo en la otra empresa."
                  : `${deshabilitados} empleados deshabilitados se darán de alta como activos en la otra empresa.`
              }
            />
          )}

          <div style={{ marginTop: 12, textAlign: "right" }}>
            <ButtonItem
              type="primary"
              verde
              icon={<DownloadOutlined />}
              disabled={!aExportar.length || cargando || exportando}
              onClick={() => this.exportar(aExportar)}
            >
              {aExportar.length === 1 ? "Exportar 1 empleado" : `Exportar ${aExportar.length} empleados`}
            </ButtonItem>
          </div>
        </div>
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  empresaNombre: state.empresa.nombre,
});

export default connect(mapStateToProps)(ModalExportarEmpleados);
